import { Color, Flex, FontSize, FontWeight, SkeletonBox } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { BarChart as BarGraph, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { SALES_REPORT_GRAPH_ELEMENT_ID } from '../../../../constants/salesReportGraphElementId'
import { useSalesReportStatistics } from '../../contexts/salesReportStatisticsContext'
import { SalesReportCartesianLine, SalesReportCartesianLineProps } from './elements/SalesReportCartesianLine'
import { SalesReportGraphHeader } from './elements/SalesReportGraphHeader'
import { SalesReportGraphTooltip } from './elements/SalesReportGraphTooltip'
import { GraphBarType } from './enums/graphBarType'
import { useSalesReportGraph } from './hooks/useSalesReportGraph'
import * as Styled from './styles'
import { generateBar } from './utils/generateBar'

const BAR_MAX_SIZE = 60
const CHART_CATEGORY_GAP = 4
const CHART_HEIGHT = 200
const CHART_OFFSET_TOP = 18
const CHART_SPACING = 6
const CHART_Y_AXIS_MARGIN = -4
const HORIZONTAL_OFFSET_LEFT = 10
const TOOLTIP_OFFSET = 20

export const SalesReportGraph = (): ReactElement => {
  const { graphData, isLoading: isSalesReportStatisticsLoading, shouldShowUnpaid, variant } = useSalesReportStatistics()
  const {
    barSize,
    isLoading: isSalesReportGraphLoading,
    layout,
    xAxisProps,
    yAxisProps,
  } = useSalesReportGraph({ graphData, variant })

  const isLoading = isSalesReportGraphLoading || isSalesReportStatisticsLoading
  const isVerticalGraph = variant === 'overview'

  const renderSalesReportCartesianLine = useCallback(
    (props: SalesReportCartesianLineProps) => <SalesReportCartesianLine {...props} layout={layout.bar} />,
    [layout],
  )

  return (
    <Styled.SalesReportGraphWrapper id={SALES_REPORT_GRAPH_ELEMENT_ID}>
      <Flex flexDirection="column">
        <SalesReportGraphHeader />
        {isLoading ? (
          <SkeletonBox fullWidth height={CHART_HEIGHT} />
        ) : (
          <ResponsiveContainer height={CHART_HEIGHT} width="100%">
            <BarGraph
              barCategoryGap={CHART_CATEGORY_GAP}
              data={graphData}
              layout={layout.graph}
              margin={{ top: CHART_OFFSET_TOP, right: isVerticalGraph ? 0 : HORIZONTAL_OFFSET_LEFT }}
              maxBarSize={BAR_MAX_SIZE}
              barSize={barSize}
            >
              <XAxis
                {...xAxisProps}
                axisLine={false}
                color={Color.Grey500}
                dy={isVerticalGraph ? CHART_SPACING : 0}
                fontWeight={FontWeight.Regular}
                fontSize={FontSize.TextBodySM}
                tickLine={false}
              />
              <YAxis
                {...yAxisProps}
                axisLine={false}
                dx={-CHART_SPACING}
                tickLine={false}
                tickMargin={CHART_Y_AXIS_MARGIN}
              />
              {generateBar(GraphBarType.Paid, layout.bar)}
              {shouldShowUnpaid && generateBar(GraphBarType.Unpaid, layout.bar)}
              <CartesianGrid
                {...(isVerticalGraph
                  ? { horizontal: renderSalesReportCartesianLine, vertical: false }
                  : { vertical: renderSalesReportCartesianLine, horizontal: false })}
              />
              <Tooltip content={SalesReportGraphTooltip} cursor={false} offset={TOOLTIP_OFFSET} />
            </BarGraph>
          </ResponsiveContainer>
        )}
      </Flex>
    </Styled.SalesReportGraphWrapper>
  )
}
